import { HyperliquidUserRole } from "@cede/types";
import { AIRDROP_FARMING_EXCHANGE_CONFIGS } from "../cexs/airdrop-farming-exchange-configs";

export const HYPERLIQUID_ZERO_ADDRESS = "0x0000000000000000000000000000000000000000" as const;

export const HYPERLIQUID_DEFAULT_CHAIN_ID = 42161;
export const HYPERLIQUID_DEFAULT_CHAIN_ID_HEX = "0xa4b1";

export const HYPERLIQUID_CHAIN_MAINNET = "Mainnet";
export const HYPERLIQUID_CHAIN_TESTNET = "Testnet";

// ===== APPROVAL TYPES ==========================================================
export const APPROVE_AGENT_PRIMARY = "HyperliquidTransaction:ApproveAgent";

// EIP-712 domain for user-signed actions
export const HYPERLIQUID_APPROVAL_DOMAIN = {
  name: "HyperliquidSignTransaction",
  version: "1",
  chainId: Number.parseInt(HYPERLIQUID_DEFAULT_CHAIN_ID_HEX, 16),
  verifyingContract: HYPERLIQUID_ZERO_ADDRESS,
};

// EIP-712 types for ApproveAgent (user-signed), not found in docs/SDK thanks GPT
export const HYPERLIQUID_APPROVAL_TYPES = {
  [APPROVE_AGENT_PRIMARY]: [
    { name: "hyperliquidChain", type: "string" },
    { name: "agentAddress", type: "address" },
    { name: "agentName", type: "string" },
    { name: "nonce", type: "uint64" },
  ],
};

export interface ApprovalMessage extends Record<string, string | number | bigint> {
  hyperliquidChain: typeof HYPERLIQUID_CHAIN_MAINNET | typeof HYPERLIQUID_CHAIN_TESTNET;
  agentAddress: string;
  agentName: string;
  nonce: bigint;
}
// ===============================================================================

// ===== BUILDER FEE APPROVAL TYPES =============================================
export const APPROVE_BUILDER_FEE_PRIMARY = "HyperliquidTransaction:ApproveBuilderFee";

export const HYPERLIQUID_BUILDER_FEE_TYPES = {
  [APPROVE_BUILDER_FEE_PRIMARY]: [
    { name: "hyperliquidChain", type: "string" },
    { name: "maxFeeRate", type: "string" },
    { name: "builder", type: "address" },
    { name: "nonce", type: "uint64" },
  ],
};

export interface BuilderFeeApprovalMessage extends Record<string, string | number | bigint> {
  hyperliquidChain: typeof HYPERLIQUID_CHAIN_MAINNET | typeof HYPERLIQUID_CHAIN_TESTNET;
  maxFeeRate: string;
  builder: `0x${string}`;
  nonce: bigint;
}
// ==============================================================================

// ===== HIP-3 BUILDER FEE CONSTANTS ============================================
// Derived from AIRDROP_FARMING_EXCHANGE_CONFIGS.hyperliquid (single source of truth).
// These aliases exist for backward compatibility with SDK ccxt adapters and hub-v2.
const _hl = AIRDROP_FARMING_EXCHANGE_CONFIGS.hyperliquid;

/** Cede builder address for HIP-3 DEX order routing. */
export const HIP3_BUILDER_ADDRESS = _hl.BUILDER_ADDRESS;

/** Builder fee rate as a human-readable string (e.g. "0.005%"). */
export const HIP3_BUILDER_FEE_RATE = `${_hl.BUILDER_FEE_BPS / 100}%`;

/** Builder fee in tenths of basis points (e.g. 5 = 0.005%). Used in CCXT order params. */
export const HIP3_BUILDER_FEE_INT = _hl.BUILDER_FEE_BPS * 10;
// ==============================================================================

// ===== USER SET ABSTRACTION TYPES ============================================
export const USER_SET_ABSTRACTION_PRIMARY = "HyperliquidTransaction:UserSetAbstraction";

export const HYPERLIQUID_USER_SET_ABSTRACTION_TYPES = {
  [USER_SET_ABSTRACTION_PRIMARY]: [
    { name: "hyperliquidChain", type: "string" },
    { name: "user", type: "address" },
    { name: "abstraction", type: "string" },
    { name: "nonce", type: "uint64" },
  ],
};

export type HyperliquidAbstractionMode = "standard" | "dexAbstraction" | "unifiedAccount" | "portfolioMargin";

export interface UserSetAbstractionMessage extends Record<string, string | number | bigint> {
  hyperliquidChain: typeof HYPERLIQUID_CHAIN_MAINNET | typeof HYPERLIQUID_CHAIN_TESTNET;
  user: `0x${string}`;
  abstraction: string;
  nonce: bigint;
}
// ==============================================================================

// ====== HYPERLIQUID RETURNED TYPES ==============================================
export type HyperliquidApproveResult =
  | { status: "ok"; response: { type: "default" } }
  | { status: "err"; response: string };

export type HyperliquidUserRoleResult = { role: HyperliquidUserRole } | { status: "err"; response: string };
